import { KNOT_WEIGHTS, priceAt } from "./priceDistribution";

// Same bounds as the vendored fitter.
const DELTA_FLOOR = 0.75;
const DELTA_CEIL = 1e4;
const LM_ITERATIONS = 200;

const GOLDEN = 0.618034;
const MODE_SEARCH_ITERATIONS = 28;
const DESCENT_ITERATIONS = 120;
const DESCENT_MIN_STEP = 1e-3;
const GAMMA_SEEDS = [-2, 0, 2];
const DELTA_SEEDS = [0.75, 2, 6];
const LAMBDA_SEED = 0.1;

// Recovers the normal quantile z(p) through the fit itself, so we don't duplicate normPpf.
const zOf = (fit, p) => fit.gamma + fit.delta * Math.asinh((Math.log(priceAt(fit, p)) - fit.xi) / fit.lam);

const knotsOf = (fit, weights) => fit.probs.map((p, i) => ({ z: zOf(fit, p), y: Math.log(fit.values[i]), w: weights[i] ?? 1 }));

const solve3 = (A, b) => {
  const M = A.map((row, i) => [...row, b[i]]);
  for (let k = 0; k < 3; k++) {
    let pivot = k;
    for (let i = k + 1; i < 3; i++) if (Math.abs(M[i][k]) > Math.abs(M[pivot][k])) pivot = i;
    [M[k], M[pivot]] = [M[pivot], M[k]];
    if (Math.abs(M[k][k]) < 1e-14) return null;
    for (let i = k + 1; i < 3; i++) {
      const f = M[i][k] / M[k][k];
      for (let j = k; j <= 3; j++) M[i][j] -= f * M[k][j];
    }
  }
  const x = [0, 0, 0];
  for (let i = 2; i >= 0; i--) {
    let s = M[i][3];
    for (let j = i + 1; j < 3; j++) s -= M[i][j] * x[j];
    x[i] = s / M[i][i];
  }
  return x;
};

// Re-fits a Johnson SU distribution so its median equals the p50 knot exactly. Fixing the median
// pins xi = y50 + lam*sinh(gamma/delta), so only (lam, gamma, delta) are fitted — weighted least
// squares over the remaining knots in log space, Levenberg-Marquardt, starting from the vendored
// unconstrained fit. Falls back to the original fit if the refit diverges.
export function refitWithExactMedian(fit, weights = KNOT_WEIGHTS) {
  if (!fit || fit.pointMass) return fit;
  const medianIdx = fit.probs.indexOf(0.5);
  if (medianIdx < 0) return fit;

  const y50 = Math.log(fit.values[medianIdx]);
  const knots = knotsOf(fit, weights).filter((_, i) => i !== medianIdx);

  const xiOf = (lam, gamma, delta) => y50 + lam * Math.sinh(gamma / delta);
  const cost = (lam, gamma, delta) => {
    const xi = xiOf(lam, gamma, delta);
    let c = 0;
    for (const k of knots) {
      const r = (xi + lam * Math.sinh((k.z - gamma) / delta) - k.y) * k.w;
      c += r * r;
    }
    return 0.5 * c;
  };

  let { lam, gamma, delta } = fit;
  let damping = 1e-3;
  let c0 = cost(lam, gamma, delta);
  for (let it = 0; it < LM_ITERATIONS; it++) {
    const JtJ = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0],
    ];
    const Jtr = [0, 0, 0];
    const v = gamma / delta;
    const shv = Math.sinh(v);
    const chv = Math.cosh(v);
    const xi = y50 + lam * shv;
    for (const k of knots) {
      const u = (k.z - gamma) / delta;
      const sh = Math.sinh(u);
      const ch = Math.cosh(u);
      const r = (xi + lam * sh - k.y) * k.w;
      const J = [(sh + shv) * k.w, ((lam * (chv - ch)) / delta) * k.w, ((-lam * (ch * u + chv * v)) / delta) * k.w];
      for (let a = 0; a < 3; a++) {
        Jtr[a] += J[a] * r;
        for (let b = 0; b < 3; b++) JtJ[a][b] += J[a] * J[b];
      }
    }
    const A = JtJ.map((row, i) => row.map((val, j) => (i === j ? val * (1 + damping) + 1e-12 : val)));
    const step = solve3(
      A,
      Jtr.map((val) => -val),
    );
    if (!step) break;
    const cand = [Math.max(lam + step[0], 1e-9), gamma + step[1], Math.min(Math.max(delta + step[2], DELTA_FLOOR), DELTA_CEIL)];
    const c1 = cost(...cand);
    if (c1 < c0) {
      [lam, gamma, delta] = cand;
      damping = Math.max(damping * 0.3, 1e-12);
      const converged = c0 - c1 < 1e-16 * Math.max(c0, 1e-16);
      c0 = c1;
      if (converged) break;
    } else {
      damping *= 10;
      if (damping > 1e12) break;
    }
  }

  const xi = xiOf(lam, gamma, delta);
  if (![xi, lam, gamma, delta].every(Number.isFinite)) return fit;
  return { ...fit, xi, lam, gamma, delta };
}

// Log of the price-space density (up to a constant) at log-price offset t from xi.
const logDensity = (t, lam, gamma, delta) => {
  const u = t / lam;
  const z = gamma + delta * Math.asinh(u);
  return -0.5 * z * z - 0.5 * Math.log(1 + u * u) - t;
};

// Offset of the density peak from xi in log space; independent of xi, found by golden-section
// search between the 0.02% and 99.98% quantiles.
const modeOffset = (lam, gamma, delta) => {
  let a = lam * Math.sinh((-3.5 - gamma) / delta);
  let b = lam * Math.sinh((3.5 - gamma) / delta);
  let c = b - GOLDEN * (b - a);
  let e = a + GOLDEN * (b - a);
  let fc = logDensity(c, lam, gamma, delta);
  let fe = logDensity(e, lam, gamma, delta);
  for (let i = 0; i < MODE_SEARCH_ITERATIONS; i++) {
    if (fc < fe) {
      a = c;
      c = e;
      fc = fe;
      e = a + GOLDEN * (b - a);
      fe = logDensity(e, lam, gamma, delta);
    } else {
      b = e;
      e = c;
      fe = fc;
      c = b - GOLDEN * (b - a);
      fc = logDensity(c, lam, gamma, delta);
    }
  }
  return (a + b) / 2;
};

// Re-fits a Johnson SU distribution so its density peaks exactly at the p50 knot (the displayed
// rate), fitting all knots by weighted least squares in log space. The mode has no closed form, so
// xi is solved numerically per candidate and (lam, gamma, delta) are found by coordinate descent
// from several seeds. Falls back to the given fit if the refit diverges.
export function refitWithExactMode(fit, weights = KNOT_WEIGHTS) {
  if (!fit || fit.pointMass) return fit;
  const modeIdx = fit.probs.indexOf(0.5);
  if (modeIdx < 0) return fit;

  const yMode = Math.log(fit.values[modeIdx]);
  const knots = knotsOf(fit, weights);
  const xiOf = (lam, gamma, delta) => yMode - modeOffset(lam, gamma, delta);
  const cost = (lam, gamma, delta) => {
    const xi = xiOf(lam, gamma, delta);
    let c = 0;
    for (const k of knots) {
      const r = (xi + lam * Math.sinh((k.z - gamma) / delta) - k.y) * k.w;
      c += r * r;
    }
    return c;
  };

  const descend = (lam0, gamma0, delta0) => {
    let best = { c: cost(lam0, gamma0, delta0), lam: lam0, gamma: gamma0, delta: delta0 };
    let step = 0.5;
    for (let it = 0; it < DESCENT_ITERATIONS; it++) {
      let improved = false;
      for (const [dl, dg, dd] of [
        [step, 0, 0],
        [-step, 0, 0],
        [0, step, 0],
        [0, -step, 0],
        [0, 0, step],
        [0, 0, -step],
      ]) {
        const cand = [best.lam * Math.exp(dl), best.gamma + dg, Math.min(Math.max(best.delta * Math.exp(dd), DELTA_FLOOR), DELTA_CEIL)];
        const c = cost(...cand);
        if (c < best.c) {
          best = { c, lam: cand[0], gamma: cand[1], delta: cand[2] };
          improved = true;
        }
      }
      if (!improved) {
        step /= 2;
        if (step < DESCENT_MIN_STEP) break;
      }
    }
    return best;
  };

  let best = null;
  for (const gamma0 of [fit.gamma, ...GAMMA_SEEDS]) {
    for (const delta0 of [fit.delta, ...DELTA_SEEDS]) {
      for (const lam0 of [fit.lam, LAMBDA_SEED]) {
        const r = descend(lam0, gamma0, delta0);
        if (Number.isFinite(r.c) && (!best || r.c < best.c)) best = r;
      }
    }
  }
  if (!best) return fit;

  const xi = xiOf(best.lam, best.gamma, best.delta);
  if (![xi, best.lam, best.gamma, best.delta].every(Number.isFinite)) return fit;
  return { ...fit, xi, lam: best.lam, gamma: best.gamma, delta: best.delta };
}
